import type {
  CaseStudyDetailResult,
  CaseStudyMetadataResult,
} from "./case-study-types";
import { typedQuery } from "../../../typed-query";

const CASE_STUDY_CARD_TYPE = {
  intercom: "intercomCaseStudyCard",
  fin: "finAiCaseStudyCard",
} as const;

export type CaseStudySite = keyof typeof CASE_STUDY_CARD_TYPE;

// fin.ai's taxonomy is references-only (#6894); intercom.com still reads the legacy slugs.
const INDUSTRIES_PROJECTION: Record<CaseStudySite, string> = {
  intercom: "industries",
  fin: `"industries": coalesce(industryTags[]->title, [])`,
};

const buildDetailQuery = (site: CaseStudySite) => `
*[_type == "sharedCaseStudy" && coalesce(slug.current, slug) == $slug && site == "${site}"][0] {
  _id,
  "slug": coalesce(slug.current, slug),
  topics,
  "keyFeatures": keyFeatures[]{ _key, ...@-> { title, link } },

  "company": company-> {
    name,
    "logoUrl": logo.asset->url,
    "logoWhiteUrl": logoWhite.asset->url,
    headquarters,
    ${INDUSTRIES_PROJECTION[site]},
  },

  hero {
    "headline": coalesce(headline, ^.title),
    hasHeroMedia,
    mediaType,
    heroMediaImage {
      "url": asset->url,
      "dimensions": asset->metadata.dimensions,
    },
    heroMediaVideo {
      heroVideoId,
      "thumbnailUrl": thumbnail.asset->url,
    },
  },

  stats[] { label, value },

  hasCompanyRepresentatives,
  "companyRepresentatives": select(hasCompanyRepresentatives != false => companyRepresentatives[] {
    name,
    jobTitle,
    "avatarUrl": avatar.asset->url,
  }),

  "contentBody": body[] {
    _key,
    title,
    "blocks": blocks[] {
      _type,
      _key,
      _type == "textBlock" => {
        content,
        "button": select(
          defined(ctaText) && defined(ctaUrl) => { "children": ctaText, "href": ctaUrl }
        ),
      },
      _type == "mediaBlock" => {
        mediaType,
        contentMediaImage {
          caption,
          aspectRatio,
          hasBackdrop,
          backdrop,
          "desktopUrl": desktopImage.asset->url,
          "desktopWidth": desktopImage.asset->metadata.dimensions.width,
          "desktopHeight": desktopImage.asset->metadata.dimensions.height,
        },
        contentMediaVideo {
          wistiaId,
          videoCaption,
          "thumbnailUrl": thumbnail.asset->url,
        },
      },
      _type == "quoteBlock" => {
        label,
        quote,
        author,
        role,
      },
      _type == "ctaBlock" => {
        label,
        title,
        shortText,
        "ctaPrimary": ctaPrimary { "children": text, "href": url },
        "ctaSecondary": ctaSecondary { "children": text, "href": url },
      },
      _type == "chartBlock" => {
        "chart": chartCreator {
          type,
          description,
          ...select(type == "bar" => ({
            "data": dataBar {
              unit,
              xaxis[]{ label, value, highlight },
              yaxis
            }
          })),
          ...select(type == "line" => ({
            "data": dataLine {
              unit,
              xaxis[]{ name, "label": axisLabel, value },
              yaxis[]{ value }
            }
          })),
          ...select(type == "stats" => ({
            "data": dataStats[]{ label, value, graphValue }
          }))
        },
      },
    },
  },

  "relatedCaseStudies": relatedCaseStudies[]-> {
    "slug": coalesce(slug.current, slug),
    site,
    "companyName": company->name,
    "companyLogoUrl": coalesce(company->logo.asset->url, company->logoWhite.asset->url),
    "headline": hero.headline,
    "summary": *[_type == "${CASE_STUDY_CARD_TYPE[site]}" && caseStudy._ref == ^._id][0].summary,
    "imageUrl": coalesce(
      *[_type == "${CASE_STUDY_CARD_TYPE[site]}" && caseStudy._ref == ^._id][0].media.asset->url,
      hero.heroMediaImage.asset->url
    ),
    stats[0..1] { label, value },
    "representative": companyRepresentatives[0] { name, jobTitle },
  },

  "metaData": metaData {
    metaTitle,
    metaDescription,
    "ogImage": ogImage.asset->url,
  },
}
`;

const buildMetadataQuery = (site: CaseStudySite) => `
*[_type == "sharedCaseStudy" && coalesce(slug.current, slug) == $slug && site == "${site}"][0] {
  "companyName": company->name,
  "headline": hero.headline,
  "metaData": metaData {
    metaTitle,
    metaDescription,
    "ogImage": ogImage.asset->url,
  },
}
`;

/**
 * Full detail query for a single shared case study.
 * Filters by slug AND site === "intercom" — returns null for fin-owned studies,
 * which the page component turns into a 404.
 */
export const caseStudyDetailQuery = typedQuery<CaseStudyDetailResult | null>(
  buildDetailQuery("intercom"),
);

/**
 * Lightweight metadata-only query for generateMetadata().
 */
export const caseStudyMetadataQuery =
  typedQuery<CaseStudyMetadataResult | null>(buildMetadataQuery("intercom"));

/**
 * Static params query — all intercom-owned case study slugs.
 */
export const caseStudyStaticParamsQuery = typedQuery<{ slug: string }[]>(`
*[_type == "sharedCaseStudy" && site == "intercom" && defined(slug)] { "slug": coalesce(slug.current, slug) }
`);

export const finCaseStudyDetailQuery = typedQuery<CaseStudyDetailResult | null>(
  buildDetailQuery("fin"),
);

export const finCaseStudyMetadataQuery =
  typedQuery<CaseStudyMetadataResult | null>(buildMetadataQuery("fin"));

/** fin.ai only builds pages for studies with a body; the rest are grid-only quote cards. */
export const finCaseStudyStaticParamsQuery = typedQuery<{ slug: string }[]>(`
*[_type == "sharedCaseStudy" && site == "fin" && defined(body[0]) && defined(slug)] { "slug": coalesce(slug.current, slug) }
`);
